import { makeStyles } from '@material-ui/core/styles';

// Font sizes kept to the app's normal scale: 0.875rem (14px) body, 0.75rem for
// small labels/chips, 1.25rem for the title — no bespoke tiny sizes. The modal
// is a fixed-height flex column; only the table region scrolls, so few docs
// leave whitespace and many docs get a scrollbar while every row stays one size.
//
// TEXT COLOUR: every text colour in this file is #000. The previous grey ramp
// (#9ca3af / #6b7280 / #4b5563 / #374151 / #1f2937) was too faint to read in
// the popups. Greys remain ONLY for non-text roles — backgrounds, borders,
// dividers — and for genuinely disabled controls, which must still look
// disabled. Hierarchy is carried by font size and weight instead of colour.
export const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 16,
    // XL modal. 1280px still crowded the 10-column document table (headers
    // wrapped onto two lines, the filter dropdowns had no room). The dialog now
    // takes almost the whole viewport — capped at 1720px so it does not stretch
    // absurdly wide on ultrawide monitors — with a 24px gutter all round so the
    // page behind it is still visible.
    width: 'calc(100vw - 48px)',
    maxWidth: 1720,
    margin: 24,
    height: 'calc(100vh - 48px)',
    maxHeight: 940,
  },
  root: {
    padding: '1.25rem 1.75rem 1rem',
    fontFamily: theme.typography.fontFamily,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    boxSizing: 'border-box',
    overflow: 'hidden',
  },

  /* ---- header ---- */
  headerRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '1rem',
    marginBottom: '1rem',
    flexShrink: 0,
  },
  headerLeft: { display: 'flex', gap: '0.85rem', alignItems: 'flex-start' },
  iconBox: {
    width: 44,
    height: 44,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    flexShrink: 0,
  },
  titleLine: { display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' },
  title: { fontSize: '1.25rem', fontWeight: 700, color: '#000', lineHeight: 1.2 },
  countBadge: {
    fontSize: '0.75rem',
    fontWeight: 500,
    color: '#000',
    background: '#f3f4f6',
    borderRadius: 20,
    padding: '2px 10px',
  },
  tag: {
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: '0.03em',
    borderRadius: 6,
    padding: '3px 8px',
  },
  subtitle: { fontSize: '0.875rem', fontWeight: 400, color: '#000', marginTop: 2 },
  contextChips: { display: 'flex', gap: '0.4rem', marginTop: '0.55rem', flexWrap: 'wrap' },
  contextChip: {
    fontSize: '0.8125rem',
    color: '#000',
    background: '#f3f4f6',
    borderRadius: 6,
    padding: '3px 10px',
  },
  headerRight: { display: 'flex', gap: '0.5rem', alignItems: 'center' },
  switchBtn: {
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '0.875rem',
    borderRadius: 8,
    borderColor: '#e5e7eb',
    color: '#000',
  },
  switchDot: { width: 8, height: 8, borderRadius: '50%', marginRight: 8 },
  closeBtn: { border: '1px solid #e5e7eb', borderRadius: 8, padding: 6 },

  /* ---- info banner ---- */
  banner: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'flex-start',
    background: '#f5f3ff',
    border: '1px solid #ede9fe',
    borderRadius: 10,
    padding: '0.7rem 0.9rem',
    fontSize: '0.875rem',
    color: '#000',
    margin: '0 0 1rem',
    flexShrink: 0,
  },

  /* ---- scope / filter bar ---- */
  scopeBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.6rem',
    flexWrap: 'wrap',
    padding: '0.5rem 0 0.75rem',
    flexShrink: 0,
  },
  scopeLabel: { fontSize: '0.75rem', fontWeight: 700, color: '#000', letterSpacing: '0.04em' },
  select: {
    // Fixed full-field width: the field's wrapper owns the width (a stable 190px),
    // and long values ellipsis inside — so selecting a value never resizes the
    // control or reflows the filter panel.
    width: '100%',
    background: '#fff',
    borderRadius: 8,
    // Extra right padding (32px) leaves room for the dropdown arrow so the
    // value never runs underneath it; the ellipsis truncates before the arrow.
    '& .MuiOutlinedInput-input': { padding: '9px 32px 9px 12px', fontSize: '0.875rem', color: '#000' },
    '& .MuiSelect-select': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      paddingRight: 32,
    },
    '&.Mui-disabled': {
      background: '#f9fafb',
    },
  },
  saveScopeBtn: {
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '0.875rem',
    borderRadius: 8,
    color: '#c2410c',
    background: '#ffedd5',
    boxShadow: 'none',
    '&:hover': { background: '#fed7aa', boxShadow: 'none' },
    // Disabled = "nothing to save yet". Muted ON PURPOSE — a disabled control
    // should not read as black body text. MUI's
    // .MuiButton-contained.Mui-disabled rule outranks this single-class one and
    // would otherwise put rgba(0,0,0,0.26) text on a grey fill.
    '&.Mui-disabled': {
      background: '#f3f4f6',
      color: '#9ca3af',
      boxShadow: 'none',
    },
  },
  // Applied on top of saveScopeBtn once the scope is confirmed. The button stays
  // disabled (nothing left to save until the scope changes) but should read as a
  // success state, not as dead UI. Declared after saveScopeBtn so JSS injects it
  // later and it wins at equal specificity.
  saveScopeBtnSaved: {
    '&.Mui-disabled': {
      background: '#dcfce7',
      color: '#15803d',
    },
  },
  saveViewBtn: {
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '0.875rem',
    borderRadius: 8,
    borderColor: '#e5e7eb',
    color: '#000',
    whiteSpace: 'nowrap',
    minWidth: 118,
  },
  clearLink: {
    fontSize: '0.875rem',
    color: '#2563eb',
    cursor: 'pointer',
    marginLeft: 'auto',
    '&:hover': { textDecoration: 'underline' },
  },
  divider: { width: 1, height: 24, background: '#e5e7eb' },

  /* ---- two-row filter panel (PPT) ---- */
  filterPanel: {
    background: '#F9FAFB',
    border: '1px solid #eef0f2',
    borderRadius: 12,
    padding: '0.9rem 1.1rem',
    margin: '0 0 0.5rem',
    flexShrink: 0,
  },
  filterRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '1rem',
    flexWrap: 'wrap',
  },
  // Faint dashed rule between filter row 1 and row 2 (as in the deck).
  filterRowDivider: { borderTop: '1px dashed #e5e7eb', margin: '0.85rem 0' },
  // A field = label stacked above its dropdown. The field grows to share the
  // (now much wider) panel evenly but is bounded top and bottom, so the
  // layout is still content-independent — selecting a value never resizes
  // the control (the select ellipsises inside a fixed box).
  filterField: {
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
    flex: '1 1 200px',
    minWidth: 180,
    maxWidth: 280,
  },
  // Matches the Product Development Dashboard's own filter labels
  // (FilterSection.jsx: fontWeight 'bold', fontSize '0.875rem', Title Case,
  // no uppercase transform, no letter-spacing) — this popup opens directly
  // over that dashboard, so its filter panel reads as part of the same screen.
  filterLabel: {
    fontSize: '0.875rem',
    fontWeight: 700,
    color: '#000',
  },
  filterSpacer: { flex: 1, minWidth: 12 },
  // Pushes a control down so it lines up with the dropdowns (which sit under a label).
  filterActionAlign: { alignSelf: 'flex-end' },
  // Orange "Save set" / "Save project" buttons (turn green once saved). Fixed
  // min-width so the label swap (Save set → Set saved) doesn't reflow the row.
  saveComboBtn: {
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '0.875rem',
    borderRadius: 8,
    color: '#c2410c',
    background: '#ffedd5',
    boxShadow: 'none',
    whiteSpace: 'nowrap',
    minWidth: 118,
    '&:hover': { background: '#fed7aa', boxShadow: 'none' },
    '&.Mui-disabled': { background: '#f3f4f6', color: '#9ca3af', boxShadow: 'none' },
  },
  saveComboBtnSaved: {
    color: '#15803d',
    background: '#dcfce7',
    '&:hover': { background: '#bbf7d0', boxShadow: 'none' },
  },

  /* ---- Document ID (hover target for the extracted-fields preview) ---- */
  docIdBtn: {
    fontSize: '0.8125rem',
    color: '#6D28D9',
    cursor: 'default',
    // Dotted underline signals "hover me" (FSD: the only hover target in the list).
    borderBottom: '1px dotted #6D28D9',
    paddingBottom: 1,
    background: 'none',
    border: 'none',
    borderBottomStyle: 'dotted',
    borderBottomWidth: 1,
    borderBottomColor: '#6D28D9',
    fontFamily: 'inherit',
  },
  // Extracted-fields hover panel. A document can carry 10-17 fields (§
  // Document Repository, kvpJson extraction), so this reads as a clean
  // two-column form rather than a loose list: labels sit in a FIXED column
  // (not "space-between", which left ragged gaps that varied with each
  // label's length) so every value starts at the same x-position and the
  // panel scans top-to-bottom in a straight line. A hairline rule between
  // rows marks where one field ends and the next begins once there are more
  // than a handful. No new colours — the existing all-#000 text policy
  // (see the file-level note above) is unchanged; clarity comes from
  // alignment, spacing and a bounded, scrollable height instead.
  extractedPanel: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    boxShadow: '0 10px 30px rgba(15,23,42,0.14)',
    padding: '0.6rem 0.8rem',
    minWidth: 300,
    maxWidth: 420,
    // Bounded so a heavily-extracted document doesn't produce a popup taller
    // than the viewport; the field list scrolls, the heading stays put.
    maxHeight: 380,
    display: 'flex',
    flexDirection: 'column',
    zIndex: 1600,
  },
  extractedHeading: {
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: '1px',
    textTransform: 'uppercase',
    color: '#000',
    marginBottom: '0.35rem',
    flexShrink: 0,
  },
  extractedList: {
    overflowY: 'auto',
    minHeight: 0,
  },
  extractedRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.75rem',
    padding: '3px 0',
  },
  // Fixed width, not flexShrink:0 + space-between — every label column lines
  // up regardless of how long an individual label is, which is what makes
  // the value column read as one straight edge instead of a ragged one.
  extractedLabel: {
    fontSize: '0.8125rem',
    color: '#000',
    flex: '0 0 130px',
    lineHeight: 1.3,
  },
  extractedValue: {
    fontSize: '0.8125rem',
    color: '#000',
    fontWeight: 500,
    lineHeight: 1.3,
    textAlign: 'right',
    wordBreak: 'break-word',
    flex: 1,
    minWidth: 0,
  },

  /* ---- row action dropdown ("Select action") ---- */
  actionBtn: {
    textTransform: 'none',
    fontWeight: 400,
    fontSize: '0.8125rem',
    borderRadius: 8,
    border: '1px solid #d1d5db',
    color: '#000',
    background: '#fff',
    // Fill the Action column and never exceed it, so the button can't push the
    // table wider than the dialog (no horizontal scroll at any width).
    width: '100%',
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
    // Keep the label text and the dropdown chevron on ONE line, chevron pinned
    // to the right (fixes the wrapped / broken look).
    '& .MuiButton-label': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'nowrap',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
    },
    '& .MuiButton-endIcon': { marginLeft: 4, flexShrink: 0 },
  },
  actionMenuItem: { fontSize: '0.8125rem', color: '#000' },
  levelIdCell: { fontSize: '0.875rem', color: '#000' },
  docTypeCell: { fontSize: '0.8125rem', color: '#000' },

  /* ---- loading state (shown until the option APIs resolve) ---- */
  loadingWrap: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.9rem',
  },
  loadingText: { fontSize: '0.9rem', color: '#000' },

  /* ---- toolbar (search + actions) ---- */
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.5rem 0 0.75rem',
    flexShrink: 0,
  },
  search: {
    flex: 1,
    maxWidth: 440,
    background: '#fff',
    borderRadius: 8,
    '& .MuiOutlinedInput-input': { padding: '9px 8px', fontSize: '0.875rem', color: '#000' },
  },
  selectedCount: { fontSize: '0.875rem', color: '#000', marginLeft: 'auto' },
  downloadBtn: {
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '0.875rem',
    borderRadius: 8,
    borderColor: '#e5e7eb',
    color: '#000',
  },
  exportBtn: {
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '0.875rem',
    borderRadius: 8,
    background: '#111827',
    color: '#fff',
    boxShadow: 'none',
    '&:hover': { background: '#1f2937', boxShadow: 'none' },
    // Disabled state kept muted on purpose (see saveScopeBtn).
    '&.Mui-disabled': { background: '#e5e7eb', color: '#9ca3af' },
  },

  /* ---- table (the only scrolling region — vertical only) ---- */
  tableWrap: {
    border: '1px solid #f1f2f4',
    borderRadius: 10,
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    // Fixed column layout keeps every column inside the dialog width, so there
    // is never a horizontal scrollbar.
    overflowX: 'hidden',
  },
  table: { tableLayout: 'fixed', width: '100%' },
  // Title Case, no uppercase transform or letter-spacing — matches how table
  // headers read everywhere else in this app (e.g. ProjectTabs/GenericTable's
  // headerCell), so this popup's table doesn't look like a one-off.
  th: {
    fontSize: '0.875rem',
    fontWeight: 700,
    color: '#000',
    borderBottom: '1px solid #eef0f2',
    background: '#fafafa',
    // MUI's size="small" cell padding is 6px 24px 6px 16px — 40px of horizontal
    // padding per column, which is what pushed the header labels onto a second
    // line. Trimmed to 10px a side; that reclaimed width goes to the labels and
    // values, so at the XL dialog width every header sits on one line.
    padding: '8px 10px',
    // The checkbox column keeps MUI's own tight padding — the trimmed padding
    // above would clip the control inside its 4% column.
    '&.MuiTableCell-paddingCheckbox': { padding: '0 0 0 4px' },
    // Some header labels (e.g. "Mosymphony ID", "Document Type", "Last Updated")
    // are wider than their fixed-percentage column at real font rendering — with
    // table-layout:fixed + the wrapper's overflowX:hidden, nowrap clipped them
    // instead of ellipsizing. Wrap onto a second line instead so the full label
    // is always visible.
    whiteSpace: 'normal',
    lineHeight: 1.3,
    wordBreak: 'normal',
  },
  td: {
    fontSize: '0.875rem',
    fontWeight: 400,
    color: '#000',
    borderBottom: '1px solid #f3f4f6',
    // Matches the header padding above so columns line up.
    padding: '8px 10px',
    // The checkbox column keeps MUI's own tight padding — the trimmed padding
    // above would clip the control inside its 4% column.
    '&.MuiTableCell-paddingCheckbox': { padding: '0 0 0 4px' },
    height: 52,
    verticalAlign: 'middle',
    // Long values wrap onto the next line, but only break a long unbreakable
    // token as a last resort — short words (e.g. the "Batch" chip) never split
    // mid-word.
    overflowWrap: 'break-word',
    wordBreak: 'normal',
  },
  docCell: { display: 'flex', alignItems: 'center', gap: '0.6rem', minWidth: 0 },
  fileBadge: {
    fontSize: '0.625rem',
    fontWeight: 700,
    color: '#dc2626',
    background: '#fee2e2',
    borderRadius: 4,
    padding: '3px 5px',
    flexShrink: 0,
  },
  // Normal weight — the document name is data, same as every other cell value.
  docName: { fontWeight: 400, color: '#000', fontSize: '0.875rem', overflowWrap: 'break-word', minWidth: 0 },
  docId: { fontSize: '0.8125rem', color: '#6D28D9' },
  levelChip: {
    fontSize: '0.75rem',
    color: '#000',
    background: '#f3f4f6',
    borderRadius: 6,
    padding: '2px 10px',
    // The chip label (Campaign / Stage / Batch) is a single word — never break it.
    display: 'inline-block',
    whiteSpace: 'nowrap',
  },
  actionsCell: { display: 'flex', flexWrap: 'nowrap', alignItems: 'center', justifyContent: 'flex-end' },
  rowAction: { border: '1px solid #e5e7eb', borderRadius: 8, padding: 5, marginLeft: 4 },
  emptyRow: { textAlign: 'center', color: '#000', fontSize: '0.875rem', padding: '2.5rem 0' },

  /* ---- footer ---- */
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.85rem 0 0.25rem',
    fontSize: '0.8125rem',
    color: '#000',
    flexShrink: 0,
  },
  footerClear: { color: '#2563eb', cursor: 'pointer', marginLeft: 8, '&:hover': { textDecoration: 'underline' } },
  footerNote: { color: '#000', fontSize: '0.8125rem' },

  /* ---- Export for BMS Release view ---- */
  exportHeaderRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '1rem',
    flexShrink: 0,
  },
  exportIconBox: {
    width: 44,
    height: 44,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    background: '#1e293b',
    flexShrink: 0,
  },
  stepDots: { display: 'flex', alignItems: 'center', gap: 6, marginRight: 4 },
  stepDot: {
    width: 24,
    height: 24,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.75rem',
    fontWeight: 700,
    color: '#000',
    background: '#f3f4f6',
  },
  stepDotActive: { color: '#fff', background: '#1e293b' },
  cmoSiteRow: { display: 'flex', alignItems: 'center', gap: '1.25rem', margin: '1.1rem 0 0.4rem', flexShrink: 0 },
  cmoSiteLabel: { fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.04em', color: '#000' },
  cmoSiteValue: { fontSize: '0.9375rem', fontWeight: 700, color: '#000' },
  cmoSiteDivider: { width: 1, height: 30, background: '#e5e7eb' },
  pkgHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    margin: '0.75rem 0 0.4rem',
    flexShrink: 0,
  },
  pkgTitle: { fontSize: '0.9375rem', fontWeight: 700, color: '#000' },
  pkgTableWrap: { border: '1px solid #f1f2f4', borderRadius: 10, overflowY: 'auto', maxHeight: 220, flexShrink: 0 },
  destinationBlock: { marginTop: '1rem', flexShrink: 0 },
  destinationLabel: { fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.04em', color: '#000', marginBottom: '0.5rem' },
  radioPill: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    padding: '2px 14px 2px 6px',
    marginBottom: '0.5rem',
    maxWidth: 320,
    cursor: 'pointer',
    fontSize: '0.875rem',
    color: '#000',
  },
  radioPillActive: { borderColor: '#8b5cf6', background: '#f5f3ff' },
  checkRow: { display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.875rem', color: '#000' },
  exportFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.85rem 0 0.25rem',
    borderTop: '1px solid #f1f2f4',
    flexShrink: 0,
  },
  cancelBtn: {
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '0.875rem',
    borderRadius: 8,
    borderColor: '#e5e7eb',
    color: '#000',
    marginRight: 8,
  },
}));
